"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import { useLightbox } from "./lightbox/LightboxContext";

const INT_IMAGES = [
  "/assets/studio-interior.jpg",
  "/assets/1br-living.jpg",
  "/assets/1br-bedroom.jpg",
  "/assets/1br-plus-interior.jpg",
];

export function Interiors() {
  const t = useTranslations();
  const { open } = useLightbox();
  const swiperRef = useRef<SwiperClass | null>(null);
  const [index, setIndex] = useState(0);

  const interiors = t.raw("interiors") as { title: string; note: string }[];
  const slides = INT_IMAGES.map((src, i) => ({ src, ...interiors[i] }));

  const goTo = (i: number) => {
    swiperRef.current?.slideTo(i);
    setIndex(i);
  };

  return (
    <section
      className="mx-auto max-w-[1280px] px-[clamp(20px,4vw,40px)] py-[clamp(72px,9vw,120px)]"
    >
      <div
        data-reveal
        className="mb-[clamp(32px,4vw,48px)] flex flex-wrap items-end justify-between gap-[clamp(32px,4vw,48px)]"
      >
        <div className="max-w-[56ch]">
          <p className="mb-[18px] text-xs uppercase tracking-[.2em] text-accent">
            {t("intKicker")}
          </p>
          <h2 className="text-[clamp(28px,3.4vw,44px)] leading-[1.12]">
            {t("intTitle")}
          </h2>
          <p className="mt-4 text-[15.5px] leading-[1.7] font-light text-muted">
            {t("intSub")}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => swiperRef.current?.slidePrev()}
            aria-label={t("prev")}
            className="flex h-[38px] w-[38px] items-center justify-center rounded-[3px] border border-ink/[.16] bg-none text-[16px] transition-colors hover:border-ink hover:bg-ink hover:text-white"
          >
            ←
          </button>
          <button
            type="button"
            onClick={() => swiperRef.current?.slideNext()}
            aria-label={t("next")}
            className="flex h-[38px] w-[38px] items-center justify-center rounded-[3px] border border-ink/[.16] bg-none text-[16px] transition-colors hover:border-ink hover:bg-ink hover:text-white"
          >
            →
          </button>
        </div>
      </div>

      <div
        data-reveal
        className="relative aspect-video w-full overflow-hidden border border-ink/10 bg-surface"
      >
        <Swiper
          onSwiper={(s) => (swiperRef.current = s)}
          onSlideChange={(s) => setIndex(s.activeIndex)}
          className="h-full w-full"
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.src}>
              <div className="relative h-full w-full">
                <Image
                  src={slide.src}
                  alt={slide.title}
                  fill
                  sizes="100vw"
                  className="object-cover"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <button
          type="button"
          onClick={() =>
            open(
              slides.map((s) => ({ src: s.src, caption: s.title })),
              index,
            )
          }
          aria-label="Fullscreen"
          className="absolute right-4 top-4 z-10 flex h-[38px] w-[38px] items-center justify-center rounded-[3px] border-0 bg-ink/70 text-[22px] leading-none text-white transition-colors hover:bg-ink"
        >
          +
        </button>
      </div>

      <div data-reveal className="mt-[18px] flex flex-wrap gap-2.5">
        {slides.map((slide, i) => {
          const active = index === i;
          return (
            <button
              key={slide.src}
              type="button"
              onClick={() => goTo(i)}
              aria-pressed={active}
              className="rounded-[3px] border px-4 py-3 text-[13.5px] font-medium transition-colors"
              style={{
                borderColor: active ? "var(--color-accent)" : "rgba(26,29,33,.16)",
                background: active ? "var(--color-accent)" : "transparent",
                color: active ? "#fff" : "#5A626B",
              }}
            >
              {slide.title}
            </button>
          );
        })}
      </div>
      <p data-reveal className="mt-4 max-w-[60ch] text-[14.5px] leading-[1.6] font-light text-muted">
        {slides[index]?.note}
      </p>
    </section>
  );
}
